import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import {
  useCreateDealMutation,
  useDeleteDealMutation,
  useUpdateDealMutation,
  CreateDealInput,
  UpdateDealInput,
  DealPropertiesInput,
} from '@/lib/graphql/types-and-hooks';
import { buildBatchDeleteDealsMutation } from '@/lib/graphql/batch';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { trackEvent } from '@/lib/posthog';
import { isLovablePreview } from '@/lib/utils/environment';
import { toast } from 'sonner';
import { useCanMutate, assertCanMutate } from '@/lib/rbac';

export interface CreateDealParams {
  properties?: DealPropertiesInput;
  customProperties?: Record<string, any>;
}

export interface UpdateDealParams {
  id: string;
  properties?: Partial<DealPropertiesInput>;
  customProperties?: Record<string, any>; // Converted to [{ key, value }] before sending
  /**
   * Suppress the generic destructive error toast so the caller can surface the
   * backend message its own way (e.g. the stage-move guard, which shows the
   * CrmApiError inline and rolls back the optimistic move). The failure is still
   * returned as `{ success: false, error }`.
   */
  suppressErrorToast?: boolean;
}

export const useDealMutations = () => {
  const { t } = useTranslation();
  const workspaceId = useWorkspaceId();
  const canMutate = useCanMutate();
  const apolloClient = useApolloClient();
  const [createDealMutation, { loading: creating }] = useCreateDealMutation();
  const [deleteDealMutation, { loading: deleting }] = useDeleteDealMutation();
  const [updateDealMutation, { loading: updating }] = useUpdateDealMutation();

  const createDeal = useCallback(
    async (params: CreateDealParams) => {
      if (!assertCanMutate(canMutate)) return { success: false };
      if (!workspaceId) {
        throw new Error('Workspace ID is required');
      }

      if (isLovablePreview()) {
        return { success: true };
      }

      try {
        // coffeeCustomProperties is GenericScalar — backend expects a flat dict
        const coffeeCustomProps = params.customProperties
          ? Object.fromEntries(
              Object.entries(params.customProperties).map(([key, value]) => [key, String(value ?? '')])
            )
          : undefined;

        const input: CreateDealInput = {
          workspaceId,
          properties: params.properties || {},
          coffeeCustomProperties: coffeeCustomProps,
        };

        const result = await createDealMutation({
          variables: { input },
          refetchQueries: ['getDeals', 'getDealsAggregates'],
          awaitRefetchQueries: true,
        });

        if (result.data?.createDeal?.deal) {
          trackEvent('deal_created');
          toast.success(t('mutations.dealCreated'));
          return { success: true, data: result.data.createDeal.deal };
        }

        throw new Error(t('mutations.dealCreateFailed'));
      } catch (error) {
        console.error('Failed to create deal:', error);
        toast.error(error instanceof Error ? error.message : t('mutations.dealCreateFailed'));
        return { success: false, error };
      }
    },
    [workspaceId, createDealMutation, canMutate, t]
  );

  const deleteDeal = useCallback(
    async (id: string) => {
      if (!assertCanMutate(canMutate)) return { success: false };
      if (isLovablePreview()) {
        return { success: true };
      }

      try {
        // ⚠️ CRITICAL: Don't use extractPlainId() on entity IDs (Pattern 29, Section 4)
        await deleteDealMutation({
          variables: { id }, // Use ID as-is from GraphQL
          update: (cache) => {
            // Evict the deal so it drops out of every cached list reactively
            // (Deals grid, Kanban per-stage queries, and any detail-sheet
            // deal lists). Callers that also reconcile (DealsPage deletedIds +
            // refetch) still work — this just makes delete correct everywhere
            // else, e.g. deleting from the global-nav detail sheet.
            cache.evict({ id: cache.identify({ __typename: 'Deal', id }) });
            cache.gc();
          },
          // Keep the stat cards (Open Pipeline / Weighted Forecast / Closed Won)
          // in sync — they read from the separate aggregate query, which
          // eviction alone doesn't recompute. Mirrors createDeal.
          refetchQueries: ['getDealsAggregates'],
        });

        toast.success(t('mutations.dealDeleted'));
        return { success: true };
      } catch (error) {
        console.error('Failed to delete deal:', error);
        toast.error(t('mutations.dealDeleteFailed'));
        return { success: false, error };
      }
    },
    [deleteDealMutation, canMutate, t]
  );

  // Batch delete — single request with one aliased deleteDeal per id.
  // Mirrors deleteDeal's guarantees (error toast + caller reconciliation) so the
  // bulk path can't silently leave deals on the server after an optimistic hide.
  const deleteDeals = useCallback(
    async (ids: string[]) => {
      if (!assertCanMutate(canMutate)) return { success: false };
      if (ids.length === 0) return { success: true };
      if (isLovablePreview()) {
        return { success: true };
      }

      try {
        const result = await apolloClient.mutate({
          mutation: buildBatchDeleteDealsMutation(ids), // ids used as-is (Pattern 29, Section 4)
          update: (cache) => {
            // Evict every deleted deal so all cached lists (grid, Kanban,
            // detail sheets) drop them reactively without a refresh.
            ids.forEach((id) => {
              cache.evict({ id: cache.identify({ __typename: 'Deal', id }) });
            });
            cache.gc();
          },
          // Keep the aggregate stat cards in sync (see deleteDeal).
          refetchQueries: ['getDealsAggregates'],
        });

        // Each alias returns { success }; a falsy success means the backend
        // rejected that delete even though the request itself didn't throw.
        const data = (result.data ?? {}) as Record<string, { success?: boolean } | null>;
        const failed = ids.filter((_, i) => !data[`delete_${i}`]?.success);
        if (failed.length > 0) {
          throw new Error(`Backend failed to delete ${failed.length} of ${ids.length} deals`);
        }

        toast.success(t('mutations.dealsDeleted', { count: ids.length }));
        return { success: true };
      } catch (error) {
        console.error('Failed to delete deals:', error);
        toast.error(t('mutations.dealsDeleteFailed'));
        return { success: false, error };
      }
    },
    [apolloClient, canMutate, t]
  );

  const updateDeal = useCallback(
    async (params: UpdateDealParams) => {
      if (!assertCanMutate(canMutate)) return { success: false };
      if (isLovablePreview()) {
        return { success: true };
      }

      try {
        // coffeeCustomProperties is GenericScalar — backend expects a flat dict
        const coffeeCustomProps = params.customProperties
          ? Object.fromEntries(
              Object.entries(params.customProperties).map(([key, value]) => [key, String(value ?? '')])
            )
          : undefined;

        const input: UpdateDealInput = {
          id: params.id, // Use ID as-is
          workspaceId: workspaceId || undefined,
          properties: params.properties || {}, // ⚠️ Always use {} not null — prevents backend NoneType error
          coffeeCustomProperties: coffeeCustomProps,
        };

        const result = await updateDealMutation({
          variables: { input },
          // No refetchQueries for getDeals — the mutation returns full DealFields + DealRelations
          // which updates the Apollo normalized cache. Inline edits use cellEdits local state for
          // immediate UI feedback. Refetching getDeals would reset pagination.
          refetchQueries: ['getDealsAggregates'],
        });

        if (result.data?.updateDeal?.deal) {
          return { success: true, data: result.data.updateDeal.deal };
        }

        throw new Error(t('mutations.dealUpdateFailed'));
      } catch (error) {
        console.error('Failed to update deal:', error);
        if (!params.suppressErrorToast) {
          toast.error(error instanceof Error ? error.message : t('mutations.dealUpdateFailed'));
        }
        return { success: false, error };
      }
    },
    [updateDealMutation, workspaceId, canMutate, t]
  );

  return {
    createDeal,
    deleteDeal,
    deleteDeals,
    updateDeal,
    creating,
    deleting,
    updating,
  };
};
